import React, { useState } from 'react';
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Badge,
  Chip,
  IconButton,
  Divider
} from '@mui/material';
 
import { colorPrimary } from '../../Styles/Colors';

const TabSingleUnit = ({
  label = "Tab",
  value = 0,
  selected = false,
  disabled = false,
  icon = null,
  badge = null,
  closable = false,
  variant = "primary", // primary, secondary
  type = "label", // "icon-and-label", "icon-only", "label-only"
  size = "medium", // small, medium, large
  orientation = "horizontal", // horizontal, vertical
  showDivider = false,
  onClick = () => {},
  onClose = () => {},
  textStyle,
  sx = {},
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = (event) => {
    if (!disabled) {
      onClick(event, value);
    }
  };

  const handleClose = (event) => {
    event.stopPropagation();
    onClose(event, value);
  };

  const getTabStyles = () => {
    const isPrimary = variant === 'primary';
    const isSecondary = variant === 'secondary';
    
    const baseStyles = {
      display: 'flex',
      flexDirection: type === 'icon-only' ? 'row' : 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textTransform: 'none',
      fontWeight: 400,
      fontSize: size === 'small' ? '12px' : size === 'large' ? '16px' : '14px',
      minHeight: size === 'small' ? '40px' : size === 'large' ? '56px' : '48px',
      padding: type === 'icon-only' ? '12px' : '12px 16px 8px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      transition: 'all 0.2s ease-in-out',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: 0,
      position: 'relative',
    };

    if (isPrimary) {
      return {
        ...baseStyles,
        color: selected ? colorPrimary : '#49454F',
        '&:hover': {
          backgroundColor: disabled ? 'transparent' : 'rgba(142, 78, 198, 0.04)',
          color: disabled ? undefined : colorPrimary
        }
      };
    }

    if (isSecondary) {
      return {
        ...baseStyles,
        color: selected ? '#1976D2' : '#6B7280',
        '&:hover': {
          backgroundColor: disabled ? 'transparent' : 'rgba(25, 118, 210, 0.04)',
          color: disabled ? undefined : '#1976D2'
        }
      };
    }

    return baseStyles;
  };

  const renderTabContent = () => {
    const isPrimary = variant === 'primary';
    const dotColor = isPrimary ? colorPrimary : '#1976D2';
    const borderColor = isPrimary ? colorPrimary : '#1976D2';

    // Icon only variant
    if (type === 'icon-only') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {selected && (
            <Box
              sx={{
                position: 'absolute',
                top: '-12px',
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: dotColor
              }}
            />
          )}
          
          {icon && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: selected ? dotColor : 'inherit'
            }}>
              {icon}
            </Box>
          )}

          {/* Bottom border line for selected state */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '24px',
              height: '2px',
              backgroundColor: selected ? borderColor : 'transparent',
              borderRadius: '1px'
            }}
          />
        </Box>
      );
    }

    // Label only variant
    if (type === 'label-only') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            position: 'relative'
          }}
        >
          {/* Dot indicator for selected state */}
          <Box
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: selected ? dotColor : 'transparent',
              marginBottom: '4px'
            }}
          />
          
          <Typography
            component="span"
            sx={{
              fontSize: 'inherit',
              fontWeight: 'inherit',
              color: selected ? dotColor : 'inherit',
              textAlign: 'center',
              ...textStyle
            }}
          >
            {label}
          </Typography>

          {/* Bottom border line for selected state */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '-8px',
              left: 0,
              right: 0,
              height: '2px',
              backgroundColor: selected ? borderColor : 'transparent',
              borderRadius: '1px'
            }}
          />
        </Box>
      );
    }

    // Icon and label variant
    if (type === 'icon-and-label') {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.5,
            position: 'relative'
          }}
        >
          {/* Dot indicator for selected state */}
          <Box
            sx={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: selected ? dotColor : 'transparent',
              marginBottom: '4px'
            }}
          />
          
          {icon && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              color: selected ? dotColor : 'inherit',
              marginBottom: '4px'
            }}>
              {icon}
            </Box>
          )}
          
          <Typography
            component="span"
            sx={{
              fontSize: 'inherit',
              fontWeight: 'inherit',
              color: selected ? dotColor : 'inherit',
              textAlign: 'center',
              ...textStyle
            }}
          >
            {label}
          </Typography>

          {/* Bottom border line for selected state */}
          <Box
            sx={{
              position: 'absolute',
              bottom: '-8px',
              left: 0,
              right: 0,
              height: '2px',
              backgroundColor: selected ? borderColor : 'transparent',
              borderRadius: '1px'
            }}
          />
        </Box>
      );
    }

    // Default fallback (label-only)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
          position: 'relative'
        }}
      >
        <Box
          sx={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: selected ? dotColor : 'transparent',
            marginBottom: '4px'
          }}
        />
        
        <Typography
          component="span"
          sx={{
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: selected ? dotColor : 'inherit',
            textAlign: 'center',
            ...textStyle
          }}
        >
          {label}
        </Typography>

        <Box
          sx={{
            position: 'absolute',
            bottom: '-8px',
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: selected ? borderColor : 'transparent',
            borderRadius: '1px'
          }}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: variant === 'vertical' ? 'column' : 'row',
        alignItems: 'center',
        position: 'relative',
        ...sx
      }}
    >
      <Box
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={getTabStyles()}
        {...props}
      >
        {renderTabContent()}
      </Box>

      {showDivider && variant !== 'vertical' && (
        <Divider 
          orientation="vertical" 
          sx={{ 
            height: '24px', 
            marginX: 1,
            borderColor: '#E0E0E0'
          }} 
        />
      )}

      {showDivider && variant === 'vertical' && (
        <Divider 
          sx={{ 
            width: '100%', 
            marginY: 1,
            borderColor: '#E0E0E0'
          }} 
        />
      )}
    </Box>
  );
};

export default TabSingleUnit;
